import {
  IMPORT_STAGES,
  type BillResponse,
  type GoodsReceiptResponse,
  type ImportOrderResponse,
  type PurchaseOrderResponse,
  type PurchaseReturnResponse,
} from '@palaccount/contracts';

import type { LifecycleStep, StepState } from '@/components/document-list/types';
import { isPositiveAmount } from '@/lib/money';

/**
 * Wave 3B — the PURCHASING chain as each document sees it, for the shared
 * `LifecycleStepper`: Purchase order → Receipt → Bill → Payment, read from
 * the links and figures the row carries. The same three conventions as the
 * sales side (`sales-steppers.ts`): a stage behind with no document is
 * SKIPPED, a stage with a document LINKS, and the next sensible act IS the
 * act (D-09) — the receipt or bill a sent order still wants, the payment a
 * posted bill still wants.
 */

const step = (
  key: string,
  state: StepState,
  extra: Partial<Pick<LifecycleStep, 'href' | 'action' | 'permission'>> = {},
): LifecycleStep => ({ key, labelKey: `po.stage.${key}`, state, ...extra });

const openOrder = (status: PurchaseOrderResponse['status']) =>
  status === 'sent' || status === 'partially_received';

/** PUR-03 — Purchase order → Receipt → BILL → Payment. */
export function billSteps(bill: BillResponse): LifecycleStep[] {
  const cancelled = bill.status === 'cancelled';
  const paid = bill.status === 'posted' && bill.fullyPaid;
  const payable = bill.status === 'posted' && !bill.fullyPaid;
  return [
    bill.purchaseOrderId
      ? step('purchase_order', 'done', { href: `/purchases/orders?selected=${bill.purchaseOrderId}` })
      : step('purchase_order', 'skipped'),
    // `receivedTotal` is null exactly while no receipt has POSTED against the order.
    step('receipt', bill.purchaseOrderId && bill.receivedTotal !== null ? 'done' : 'skipped'),
    step('bill', 'current'),
    step('payment', cancelled ? 'skipped' : paid ? 'done' : 'pending', {
      ...(payable
        ? {
            action: {
              labelKey: 'bills.stepAct.payment',
              to: `/accounting/payments/new?partnerId=${bill.partnerId}&billId=${bill.id}`,
            },
            permission: { permission: 'accounting.create', reasonKey: 'perm.cannot.createPayments' },
          }
        : {}),
    }),
  ];
}

/** PUR-01 — PURCHASE ORDER → Receipt → Bill → Payment; the next two stages are acts on a sent order. */
export function purchaseOrderSteps(order: PurchaseOrderResponse): LifecycleStep[] {
  const received = order.receivedTotal !== null;
  const billed = isPositiveAmount(order.billedTotal);
  const billable = openOrder(order.status) || order.status === 'received';
  return [
    step('purchase_order', 'current'),
    step('receipt', received ? 'done' : 'pending', {
      ...(!received && openOrder(order.status)
        ? {
            action: { labelKey: 'po.stepAct.receipt', to: `/purchases/receipts/new?poId=${order.id}` },
            permission: { permission: 'purchasing.create', reasonKey: 'perm.cannot.createReceipts' },
          }
        : {}),
    }),
    step('bill', billed ? 'done' : 'pending', {
      ...(!billed && billable
        ? {
            action: { labelKey: 'po.stepAct.bill', to: `/purchases/bills/new?poId=${order.id}` },
            permission: { permission: 'purchasing.create', reasonKey: 'perm.cannot.createBills' },
          }
        : {}),
    }),
    step('payment', 'pending'),
  ];
}

/** PUR-02 — Purchase order → RECEIPT → Bill → Payment; the bill is the act once the goods are in. */
export function goodsReceiptSteps(receipt: GoodsReceiptResponse): LifecycleStep[] {
  return [
    step('purchase_order', 'done', { href: `/purchases/orders?selected=${receipt.purchaseOrderId}` }),
    step('receipt', 'current'),
    step('bill', 'pending', {
      ...(receipt.status === 'posted'
        ? {
            action: { labelKey: 'po.stepAct.bill', to: `/purchases/bills/new?poId=${receipt.purchaseOrderId}` },
            permission: { permission: 'purchasing.create', reasonKey: 'perm.cannot.createBills' },
          }
        : {}),
    }),
    step('payment', 'pending'),
  ];
}

const returnStep = (key: string, state: StepState, extra: Partial<Pick<LifecycleStep, 'href'>> = {}): LifecycleStep => ({
  key,
  labelKey: `preturn.stage.${key}`,
  state,
  ...extra,
});

/**
 * PUR-04 — the debit note's OWN chain: Bill → RETURN → Stock out → Debit
 * note / refund. Stock out is greyed on a value-only return and when the
 * goods were never restocked (nothing on the shelf to send back); the last
 * stage is done once the supplier credited or refunded (BR-PUR-04-01/-02).
 */
export function purchaseReturnSteps(note: PurchaseReturnResponse): LifecycleStep[] {
  const cancelled = note.status === 'cancelled';
  const left = note.status !== 'draft' && !cancelled;
  const settled = note.status === 'credited' || note.status === 'refunded';
  return [
    returnStep('bill', 'done', { href: `/purchases/bills/${note.billId}` }),
    returnStep('return', 'current'),
    returnStep('stockOut', note.valueOnly || !note.restocked || cancelled ? 'skipped' : left ? 'done' : 'pending'),
    returnStep('settlement', cancelled ? 'skipped' : settled ? 'done' : 'pending'),
  ];
}

/**
 * PUR-06 — the seven stages of `import-stages.ts`, green behind, blue now, grey
 * ahead. Wave 5E: a CANCELLED shipment is off the rail — every stage is
 * skipped and a last "Cancelled" step is the current one.
 */
export function importSteps(order: Pick<ImportOrderResponse, 'stage'>): LifecycleStep[] {
  if (order.stage === 'cancelled') {
    return [
      ...IMPORT_STAGES.map((stage) => ({ key: stage, labelKey: `imports.stage_${stage}`, state: 'skipped' as const })),
      { key: 'cancelled', labelKey: 'imports.stage_cancelled', state: 'current' as const },
    ];
  }
  const current = IMPORT_STAGES.indexOf(order.stage);
  return IMPORT_STAGES.map((stage, index) => ({
    key: stage,
    labelKey: `imports.stage_${stage}`,
    state: index < current ? 'done' : index === current ? 'current' : 'pending',
  }));
}
